import { inject, Injectable } from '@angular/core';
import { BaseDomainModel } from '../models/base/base-domain-model';
import { AccountAPI, MemberAccountsFilter } from '../api/account-api';
import { Observable } from 'rxjs';
import { MemberAccount } from '../models/transactions/member-account';
import { map } from 'rxjs/operators';
import { MemberAccountFundAllocation } from '../models/transactions/member-account-fund-allocation';
import { MemberAccountFundDetails } from '../models/transactions/member-account-fund-details';
import { MemberAccountContributionBalance } from '../models/transactions/member-account-contribution-balance';
import { MemberAccountBalanceByLockedStatus } from '../models/transactions/member-account-balance-by-locked-status';
import { EmployersAPI } from '../api/employers-api';
import { EmployerLogo } from '../models/users/dto/employer-logo';
import { InvestmentInstructions } from '../models/transactions/investment-instructions';
import { AccountTypeIdEnum } from '../models/enum/shared/account-type-id.enum';
import { MemberAccountBalanceByFund } from '../models/transactions/member-account-balance-by-fund';

@Injectable({
  providedIn: 'root'
})
export class AccountDomainModel extends BaseDomainModel {
  private accountAPI = inject(AccountAPI);
  private employersAPI = inject(EmployersAPI);

  public ipGetMemberAccounts(memberId: number, filter?: MemberAccountsFilter): Observable<MemberAccount[]> {
    return this.accountAPI.ipGetMemberAccounts(memberId, filter).pipe(map(response => response.value));
  }

  public ipGetMemberAccountsByAccountType(
    memberId: number,
    accountTypeId: AccountTypeIdEnum,
    filter?: Pick<MemberAccountsFilter, 'excludeStruck'>
  ): Observable<MemberAccount[]> {
    return this.accountAPI
      .ipGetMemberAccountsByAccountType(memberId, accountTypeId, filter)
      .pipe(map(response => response.value));
  }

  public mpGetAccounts(accountTypeId?: AccountTypeIdEnum): Observable<MemberAccount[]> {
    return this.accountAPI.mpGetAccounts(accountTypeId).pipe(map(response => response.value));
  }

  public getInternalMemberAccount(memberId: number, accountId: number): Observable<MemberAccount> {
    return this.accountAPI.getInternalMemberAccount(memberId, accountId);
  }

  public getMemberAccount(accountId: number): Observable<MemberAccount> {
    return this.accountAPI.getMemberAccount(accountId);
  }

  public mpGetEmployerLogo(employerId: number): Observable<EmployerLogo> {
    return this.employersAPI.mpGetEmployerLogo(employerId);
  }

  public getMemberAccountBalanceByLockedStatus(accountId: number): Observable<MemberAccountBalanceByLockedStatus> {
    return this.accountAPI.getMemberAccountBalanceByLockedStatus(accountId);
  }

  public ipGetMemberAccountFundAllocation(
    memberId: number,
    accountId: number[],
    excludeCash: boolean
  ): Observable<MemberAccountFundAllocation[]> {
    return this.accountAPI.ipGetMemberAccountFundAllocation(memberId, accountId, excludeCash);
  }

  public ipGetMemberAccountInvestmentInstructions(
    memberId: number,
    accountIds: number[]
  ): Observable<InvestmentInstructions[]> {
    return this.accountAPI.ipGetMemberAccountInvestmentInstructions(memberId, accountIds);
  }

  public mpGetAccountInvestmentInstructions(accountIds: number[]): Observable<InvestmentInstructions[]> {
    return this.accountAPI.mpGetAccountInvestmentInstructions(accountIds);
  }

  public mpGetAccountAllocationOfFunds(
    accountId: number[],
    excludeCash: boolean
  ): Observable<MemberAccountFundAllocation[]> {
    return this.accountAPI.mpGetAccountAllocationOfFunds(accountId, excludeCash);
  }

  public ipGetMemberAccountFundDetails(memberId: number, accountId: number): Observable<MemberAccountFundDetails[]> {
    return this.accountAPI.ipGetMemberAccountFundDetails(memberId, accountId);
  }

  public getMemberAccountFundDetails(accountId: number): Observable<MemberAccountFundDetails[]> {
    return this.accountAPI.getMemberAccountFundDetails(accountId);
  }

  public getInternalMemberAccountContributionBalances(
    memberId: number,
    accountId: number
  ): Observable<MemberAccountContributionBalance[]> {
    return this.accountAPI.getInternalMemberAccountContributionBalances(memberId, accountId);
  }

  public ipGetMemberBalancesByAccountAndFund(
    memberId: number,
    accountTypeId: AccountTypeIdEnum
  ): Observable<MemberAccountBalanceByFund[]> {
    return this.accountAPI.ipGetMemberBalancesByAccountAndFund(memberId, accountTypeId);
  }

  public getMemberAccountContributionBalances(accountId: number): Observable<MemberAccountContributionBalance[]> {
    return this.accountAPI.getMemberAccountContributionBalances(accountId);
  }

  public mpConfirmDefaultInvestmentInstructions(): Observable<any> {
    return this.accountAPI.mpConfirmDefaultInvestmentInstructions();
  }
}
